import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";

const SIZES = ["S", "M", "L", "XL"];

const fmtImg = (b) => (b ? (b.startsWith("data:") ? b : `data:image/png;base64,${b}`) : null);

// Public manufacturer spec sheet. Designed to be shared with a factory —
// no auth, self-contained, print-friendly (Ctrl+P prints cleanly).
const ManufacturerSpec = () => {
  const { shortId } = useParams();
  const [spec, setSpec] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/spec/${shortId}`)
      .then((r) => setSpec(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Spec not found"));
  }, [shortId]);

  if (error) {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center px-6">
        <div className="text-center">
          <div className="text-[10px] tracking-[0.3em] uppercase text-red-600 mb-2">// SPEC NOT FOUND</div>
          <p className="text-sm text-zinc-600">The manufacturer spec at <span className="font-mono">/spec/{shortId}</span> could not be found.</p>
        </div>
      </div>
    );
  }
  if (!spec) {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">// LOADING SPEC…</div>
      </div>
    );
  }

  const dims = spec.dimensions || {};

  return (
    <div data-testid="manufacturer-spec-page" className="min-h-screen bg-white text-black print:bg-white">
      {/* Header */}
      <header className="border-b-4 border-black bg-black text-white px-8 py-6 print:break-inside-avoid">
        <div className="max-w-[1200px] mx-auto flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#d4af37] mb-1">// MANUFACTURING SPEC · CONFIDENTIAL</div>
            <div className="font-display text-3xl text-[#d4af37]">RAW COMPANY CLOTHING</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 mt-1">
              Parent: RAW COMPANY TECH LIMITED · Companies House 17349121
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-400">Spec ID</div>
            <div className="font-mono text-lg text-[#d4af37]">{spec.spec_id}</div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mt-1">
              Drop {String(spec.drop_number || 1).padStart(2, "0")} · {new Date(spec.generated_at).toISOString().slice(0, 10)}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-8 py-10 print:py-6 space-y-10">
        {/* Product title + summary */}
        <section>
          <h1 data-testid="spec-product-name" className="font-display text-4xl mb-2">{spec.name}</h1>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
            <SpecKV label="Garment" value={`${spec.clothing_type}${spec.garment_style ? ` · ${spec.garment_style}` : ""}`} />
            <SpecKV label="Gender" value={spec.gender} />
            <SpecKV label="Colour" value={spec.color || "—"} />
            <SpecKV label="Fabric" value={spec.material || "—"} />
          </div>
          {spec.description && (
            <p className="mt-4 text-sm text-zinc-700 leading-relaxed max-w-3xl">{spec.description}</p>
          )}
        </section>

        {/* Unit split — this is the headline block */}
        <section className="border-2 border-black p-6 print:break-inside-avoid" data-testid="unit-split-block">
          <div className="text-[10px] tracking-[0.4em] uppercase text-black mb-1">// PRODUCTION ORDER</div>
          <h2 className="font-display text-2xl mb-4">
            {spec.units_per_size} × each size · {spec.total_units} units total
          </h2>
          <div className="grid grid-cols-4 gap-3">
            {SIZES.map((s) => {
              const row = (spec.unit_split || []).find((x) => x.size === s);
              return (
                <div key={s} className="border border-black p-4 text-center" data-testid={`spec-size-${s}`}>
                  <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500">Size</div>
                  <div className="font-display text-3xl mb-1">{s}</div>
                  <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500">Quantity</div>
                  <div className="font-mono text-2xl text-[#c99a1a]">× {row?.quantity ?? spec.units_per_size}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Reference images */}
        <section className="print:break-inside-avoid">
          <div className="text-[10px] tracking-[0.4em] uppercase text-black mb-3">// REFERENCE VIEWS</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: "Front", src: fmtImg(spec.image_front) },
              { label: "Back", src: fmtImg(spec.image_back) },
              { label: "Side", src: fmtImg(spec.image_side) },
            ].map((v) => (
              <div key={v.label} className="border-2 border-black">
                <div className="text-[10px] tracking-[0.3em] uppercase bg-black text-white p-2 text-center">{v.label} View</div>
                <div className="aspect-square bg-zinc-100 flex items-center justify-center">
                  {v.src ? (
                    <img src={v.src} alt={`${v.label} view of ${spec.name}`} className="w-full h-full object-contain" />
                  ) : (
                    <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-400">// no image supplied</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Dimensions table */}
        {(dims.S || dims.M || dims.L || dims.XL) && (
          <section className="print:break-inside-avoid">
            <div className="text-[10px] tracking-[0.4em] uppercase text-black mb-3">// DIMENSIONS (CM)</div>
            <div className="border-2 border-black overflow-x-auto">
              <table className="w-full font-mono text-sm">
                <thead>
                  <tr className="border-b-2 border-black bg-black text-white">
                    <th className="text-left p-3 text-[10px] tracking-[0.3em] uppercase">Measurement</th>
                    {SIZES.map((s) => <th key={s} className="text-center p-3 text-[10px] tracking-[0.3em] uppercase">{s}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const rows = new Set();
                    SIZES.forEach((s) => Object.keys(dims[s] || {}).forEach((k) => rows.add(k)));
                    const sortedRows = Array.from(rows);
                    if (sortedRows.length === 0) {
                      return (
                        <tr>
                          <td colSpan={5} className="p-4 text-center text-zinc-500 text-sm">
                            No dimensions recorded for this design yet.
                          </td>
                        </tr>
                      );
                    }
                    return sortedRows.map((k) => (
                      <tr key={k} className="border-b border-zinc-300 last:border-b-0">
                        <td className="p-3 text-zinc-800">{k}</td>
                        {SIZES.map((s) => (
                          <td key={s} className="p-3 text-center">
                            {dims[s]?.[k] ? <span className="text-[#c99a1a]">{dims[s][k]}</span> : <span className="text-zinc-400">—</span>}
                          </td>
                        ))}
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Materials */}
        {Array.isArray(spec.materials) && spec.materials.length > 0 && (
          <section className="print:break-inside-avoid">
            <div className="text-[10px] tracking-[0.4em] uppercase text-black mb-3">// PANEL MATERIALS</div>
            <div className="border-2 border-black">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-black bg-black text-white">
                    <th className="text-left p-3 text-[10px] tracking-[0.3em] uppercase">Panel</th>
                    <th className="text-left p-3 text-[10px] tracking-[0.3em] uppercase">Material</th>
                  </tr>
                </thead>
                <tbody>
                  {spec.materials.map((m, i) => (
                    <tr key={i} className="border-b border-zinc-300 last:border-b-0">
                      <td className="p-3 text-zinc-800">{m.panel || "—"}</td>
                      <td className="p-3 text-zinc-800">{m.material || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Factory notes */}
        {spec.factory_notes && (
          <section className="print:break-inside-avoid">
            <div className="text-[10px] tracking-[0.4em] uppercase text-black mb-3">// FACTORY NOTES</div>
            <pre className="border-2 border-black p-5 bg-zinc-50 text-xs text-zinc-800 whitespace-pre-wrap font-mono leading-relaxed max-h-[600px] overflow-auto">
              {spec.factory_notes}
            </pre>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t-4 border-black bg-black text-white px-8 py-6 mt-10">
        <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[10px] tracking-[0.3em] uppercase text-zinc-400">
          <div>
            © {new Date().getFullYear()} <span className="text-white">RAW COMPANY TECH LIMITED</span> ·
            <span className="text-[#d4af37] normal-case tracking-normal ml-1">Companies House 17349121</span>
          </div>
          <a href="https://rawcompanyclothing.uk" className="text-[#d4af37] hover:underline">rawcompanyclothing.uk</a>
        </div>
      </footer>

      {/* Print button — hidden when printing */}
      <button
        type="button"
        onClick={() => window.print()}
        data-testid="spec-print-btn"
        className="fixed bottom-6 right-6 print:hidden bg-black text-[#d4af37] border-2 border-[#d4af37] px-5 py-3 text-[10px] tracking-[0.3em] uppercase font-bold hover:bg-[#d4af37] hover:text-black"
      >
        ⎙ Print spec
      </button>
    </div>
  );
};

const SpecKV = ({ label, value }) => (
  <div>
    <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500">{label}</div>
    <div className="text-zinc-900 mt-0.5">{value}</div>
  </div>
);

export default ManufacturerSpec;
